import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  BeforeInstallPromptEvent,
  canOfferInstallAction,
  checkPwaInstalled,
  clearCapturedInstallPrompt,
  getCapturedInstallPrompt,
  isAndroidInstallDevice,
  isDesktopBrowser,
  isIosDevice,
  isRunningAsStandalonePwa,
  isServiceWorkerControlling,
  markPwaInstalled,
  subscribeInstallPrompt,
  syncInstallStateForBrowserContext,
  waitForInstallPrompt,
} from '../utils/pwa'
import { PwaInstallHintModal } from '../components/pwa-install/PwaInstallHintModal'

type InstallHintType =
  | 'ios'
  | 'android'
  | 'android-not-ready'
  | 'insecure'
  | 'already-installed'

type PwaInstallContextValue = {
  showInstallButton: boolean
  hintType: InstallHintType | null
  openInstall: () => Promise<void>
  closeHint: () => void
}

const PwaInstallContext = createContext<PwaInstallContextValue | null>(null)

export function PwaInstallProvider({ children }: { children: ReactNode }) {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(() => getCapturedInstallPrompt())
  const [isInstalled, setIsInstalled] = useState(isRunningAsStandalonePwa)
  const [hintType, setHintType] = useState<InstallHintType | null>(null)

  useEffect(() => {
    syncInstallStateForBrowserContext()
    setIsInstalled(isRunningAsStandalonePwa())

    const unsubscribe = subscribeInstallPrompt(setDeferredPrompt)

    return unsubscribe
  }, [])

  const showInstallButton =
    !isRunningAsStandalonePwa() && canOfferInstallAction(deferredPrompt)

  const openInstall = useCallback(async () => {
    if (await checkPwaInstalled()) {
      setIsInstalled(true)
      setHintType('already-installed')
      return
    }

    if (!window.isSecureContext) {
      setHintType('insecure')
      return
    }

    const prompt =
      deferredPrompt ??
      getCapturedInstallPrompt() ??
      (isAndroidInstallDevice() ? await waitForInstallPrompt(3000) : null)

    if (prompt) {
      await prompt.prompt()
      const choice = await prompt.userChoice
      clearCapturedInstallPrompt()
      setDeferredPrompt(null)

      if (choice.outcome === 'accepted') {
        markPwaInstalled()
        setIsInstalled(true)
        setHintType(null)
      }
      return
    }

    if (isIosDevice()) {
      setHintType('ios')
      return
    }

    if (isAndroidInstallDevice()) {
      if (!isServiceWorkerControlling()) {
        setHintType('android-not-ready')
        return
      }

      setHintType('android')
      return
    }

    if (isDesktopBrowser()) {
      setHintType('already-installed')
    }
  }, [deferredPrompt])

  const closeHint = useCallback(() => {
    setHintType(null)
  }, [])

  const value = useMemo(
    () => ({
      showInstallButton,
      hintType,
      openInstall,
      closeHint,
    }),
    [showInstallButton, hintType, openInstall, closeHint]
  )

  return (
    <PwaInstallContext.Provider value={value}>
      {children}
      <PwaInstallHintModal />
    </PwaInstallContext.Provider>
  )
}

export function usePwaInstallContext() {
  const context = useContext(PwaInstallContext)
  if (!context) {
    throw new Error('usePwaInstallContext must be used within PwaInstallProvider')
  }
  return context
}
